import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TuiRoot } from '@taiga-ui/core';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { CockpitDisplayStateService } from './cockpit/shared/services/cockpit-display-state.service';
import { AdminGuardComponent } from './core/components/admin-guard/admin-guard.component';
import { BreadcrumbComponent } from './core/components/breadcrumb/breadcrumb.component';
import { NavbarComponent } from './core/components/navbar/navbar.component';
import { SpinnerComponent } from './core/components/spinner/spinner.component';
import { AdminGuardStateService } from './core/services/admin-guard-state/admin-guard-state.service';
import { IdleService } from './core/services/idle/idle.service';
import { NavbarStateService } from './core/services/navbar-state/navbar-state.service';
import { userStore } from './core/user.store';
import { CommentModalBilatComponent } from './quotidien/components/comment-modal-bilat/comment-modal-bilat.component';
import { AlertComponent } from './shared/components/modal/alert/alert.component';
import { ConfirmComponent } from './shared/components/modal/confirm/confirm.component';
import { HeadbandComponent } from './shared/components/modal/headband/headband.component';
import { MiniAlertComponent } from './shared/components/modal/mini-alert/mini-alert.component';
import { MiniHeadbandComponent } from './shared/components/modal/mini-headband/mini-headband.component';
import { DateService } from './shared/services/date/date.service';
import { ModalService } from './shared/services/modal/modal.service';
import { WebsocketService } from './shared/services/websocket/websocket.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    NavbarComponent,
    BreadcrumbComponent,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    SpinnerComponent,
    AdminGuardComponent,
    HeadbandComponent,
    MiniHeadbandComponent,
    AlertComponent,
    ConfirmComponent,
    MiniAlertComponent,
    CommentModalBilatComponent,
    TuiRoot,
  ],
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly dateService = inject(DateService);
  private readonly websocketService = inject(WebsocketService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);
  readonly cockpitDisplayStateService = inject(CockpitDisplayStateService);
  readonly store = inject(userStore);
  readonly modal = inject(ModalService);
  readonly idle = inject(IdleService);
  private readonly navbarState = inject(NavbarStateService);
  private readonly adminGuardState = inject(AdminGuardStateService);

  get isCockpitRoute(): boolean {
    return this.router.url === '/cockpit' || this.router.url.startsWith('/cockpit/');
  }

  toggleRddMode(): void {
    this.cockpitDisplayStateService._displayState = this.cockpitDisplayStateService._displayState === 'rdd' ? 'normal' : 'rdd';
  }

  toggleRddFilters(): void {
    this.cockpitDisplayStateService._displayFilter = !this.cockpitDisplayStateService._displayFilter;
  }

  get isNavCollapse() {
    return this.navbarState.navCollapse();
  }

  constructor() {
    effect(async () => {
      if (this.store.user().in_bilat === 'true' && this.store.user().id !== 0 && !this.modal.miniHeadband.toggle()) {
        this.http
          .get<{
            bilat_id: number;
            bilat_id_seller: number;
            bilat_id_manager: number;
          }>(`${environment.apiUrl}/bilat/check-is-in-bilat`, { observe: 'response' })
          .subscribe({
            next: (res) => {
              if (res.body !== null) {
                if (res.body.bilat_id_seller === this.store.user().id) {
                  this.modal.openMiniHeadband('Vous êtes en mode bilat. Certaines fonctionnalités sont donc désactivées.', '', '', () => {});
                  this.router.navigate(['/quotidien/client/liste'], { queryParams: { onglet: 'lead' }, queryParamsHandling: 'merge' });
                } else {
                  this.modal.openMiniHeadband(
                    'Vous êtes en mode bilat. Certaines fonctionnalités sont donc désactivées.',
                    'Terminer la bilat',
                    'Mettre en pause',
                    (e) => {
                      if (e) this.stopBilatMode(res.body!.bilat_id);
                      else this.pauseBilatMode(res.body!.bilat_id);
                    },
                  );
                }
              }
            },
            error: (err) => {
              this.modal.openAlert('error', 'Erreur', 'Une erreur est survenue lors de la récupération des informations de la bilat', () =>
                this.modal.closeAlert(),
              );
              console.error(err);
            },
          });
      } else if (this.store.user().in_bilat === 'pause' && this.store.user().id !== 0) {
        this.http
          .get<{
            bilat_id: number;
            bilat_id_seller: number;
            bilat_id_manager: number;
          }>(`${environment.apiUrl}/bilat/check-is-in-bilat`, { observe: 'response' })
          .subscribe({
            next: (res) => {
              if (res.body !== null) {
                if (res.body.bilat_id_seller === this.store.user().id) {
                  this.modal.openMiniHeadband("Vous êtes en pause d'une bilat.", '', '', () => {});
                  this.router.navigate(['/quotidien/client/liste'], { queryParams: { onglet: 'lead' }, queryParamsHandling: 'merge' });
                } else {
                  this.modal.openMiniHeadband("Vous êtes en pause d'une bilat.", 'Reprendre la bilat', '', (e) => {
                    if (e) this.unpauseBilatMode(res.body!.bilat_id);
                  });
                }
              }
            },
            error: (err) => {
              this.modal.openAlert('error', 'Erreur', 'Une erreur est survenue lors de la récupération des informations de la bilat', () =>
                this.modal.closeAlert(),
              );
              console.error(err);
            },
          });
      }
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!(e as NavigationEnd).urlAfterRedirects.startsWith('/cockpit')) {
          this.cockpitDisplayStateService.reset();
        }
        this.navbarState.toggleAppList.set(true);
        if (environment.production) {
          this.swUpdate
            .checkForUpdate()
            .catch((err: any) => console.log(err))
            .then((res: any) => {
              if (res && typeof res === 'boolean') {
                this.swUpdate.activateUpdate();
                window.location.reload();
              }
            });
        }
        sessionStorage.setItem('lastUrl', (e as NavigationEnd)['url']);
        if (!(e as NavigationEnd)['url'].includes('admin') && !(e as NavigationEnd)['url'].includes('admin-check')) {
          this.adminGuardState.adminCheck.set(false);
          this.adminGuardState.authorized.set(false);
        }
      });
  }

  ngOnInit(): void {
    if (sessionStorage.getItem('userSession') !== null) {
      this.store.setUserInfos(JSON.parse(sessionStorage.getItem('userSession')!));
    }
    if (sessionStorage.getItem('accessApp') !== null) {
      this.store.setAccessApp(JSON.parse(sessionStorage.getItem('accessApp')!));
    }

    this.checkIfPendingBilatComment();

    this.websocketService.listen('reload--bilat', (data: { id_seller: number; id_bilat: number }) => {
      const { id_seller, id_bilat } = data;
      if (id_seller === this.store.user().id) {
        this.store.setBiltatMode('true');
        this.modal.openMiniHeadband('Vous êtes en mode bilat. Certaines fonctionnalités sont donc désactivées.', '', '', () => {});
        if (this.router.url.split('/')[1] !== 'quotidien') {
          this.router.navigate(['/quotidien/client/liste'], {
            queryParams: {
              onglet: 'lead',
              'lead.creation_date': this.dateService.getLastMonthStartDate() + '|' + this.dateService.getEndMonthDate(),
            },
            queryParamsHandling: 'merge',
          });
        }
      } else {
        this.store.setBiltatMode('true');
        this.modal.openMiniHeadband(
          'Vous êtes en mode bilat. Certaines fonctionnalités sont donc désactivées.',
          'Terminer la bilat',
          'Mettre en pause',
          (e) => {
            if (e) this.stopBilatMode(id_bilat);
            else this.pauseBilatMode(id_bilat);
          },
        );
      }
    });

    this.websocketService.listen('return-close_bilat--quotidien', (data: { id_bilat: number }) => {
      this.store.setBiltatMode('false');
      this.modal.closeMiniHeadband();
      this.router.navigate([`/quotidien/vendeur/rapport-bilat/${data.id_bilat}`]);
    });

    this.websocketService.listen('return-pause_bilat--quotidien', (data: { id_seller: number; id_bilat: number }) => {
      const { id_seller, id_bilat } = data;
      if (id_seller === this.store.user().id) {
        this.modal.openMiniHeadband("Vous êtes en pause d'une bilat.", '', '', () => {});
        this.store.setBiltatMode('pause');
        this.router.navigate(['/quotidien/client/liste'], { queryParams: { onglet: 'lead' }, queryParamsHandling: 'merge' });
      } else {
        this.store.setBiltatMode('pause');
        this.modal.openMiniHeadband("Vous êtes en pause d'une bilat.", 'Reprendre la bilat', '', (e) => {
          if (e) this.unpauseBilatMode(id_bilat);
        });
      }
    });

    this.websocketService.listen('redirect--to-customer-details', (data: { redirectTo: string }) => {
      this.router.navigateByUrl(data.redirectTo);
    });

    this.websocketService.listen('refresh-token--user-account', (data: { id: number }) => {
      if (data.id === this.store.user().id) {
        this.modal.openConfirm(
          'info',
          'Droits mis à jour',
          'Vos droits ont été mis à jour. Pour profiter de ces changements, veuillez vous déconnecter et vous reconnecter.',
          'Se déconnecter',
          'Plus tard',
          (confirmed) => {
            this.modal.closeConfirm();
            if (confirmed) {
              sessionStorage.clear();
              localStorage.clear();
              this.store.logout();
            }
          },
        );
      }
    });

    this.websocketService.listen('refresh-all--user-account', (data: { id: number }) => {
      if (data.id === this.store.user().id) {
        this.store.setUserInfos();
        this.store.setAccessApp();
        this.store.refreshTokenInfo();
        this.modal.openConfirm(
          'info',
          'Droits mis à jour',
          'Vos droits ont été mis à jour. Pour profiter de ces changements, veuillez vous déconnecter et vous reconnecter.',
          'Se déconnecter',
          'Plus tard',
          (confirmed) => {
            this.modal.closeConfirm();
            if (confirmed) this.store.logout();
          },
        );
      }
    });

    this.websocketService.listen('refresh application', () => window.location.reload());

    this.websocketService.listen('refresh alert-dev', () => {
      const app = this.router.url.split('/')[1];
      const appIdMap: Record<string, number> = {
        'cahier-de-liaison': 23,
        'outils-marketing': 24,
        vd: 25,
        'essai-pret': 26,
        'post-liv': 27,
        primes: 30,
        formation: 31,
        requeteur: 32,
        atterissage: 40,
        quotidien: 39,
        'stock-vn': 41,
        campagne: 48,
        'appel-apv': 51,
        vo: 49,
        carriere: 55,
        perfco: 56,
        'suivi-creance': 58,
      };
      if (appIdMap[app]) this.store.getAlertDev(appIdMap[app]);
    });
  }

  private stopBilatMode(id_bilat: number): void {
    this.http.post(`${environment.apiUrl}/bilat/close`, { id_bilat }, { observe: 'response' }).subscribe({
      next: () => {
        this.router.navigate([`/quotidien/vendeur/rapport-bilat/${id_bilat}`]);
        this.modal.closeMiniHeadband();
        localStorage.setItem('pendingBilatComment', id_bilat.toString());
        this.modal.openBilatComment(id_bilat);
      },
      error: (err) => {
        this.modal.openAlert('error', 'Erreur', 'Une erreur est survenue lors de la fermeture de la bilat', () => this.modal.closeAlert());
        console.error(err);
      },
    });
  }

  private pauseBilatMode(id_bilat: number): void {
    this.http.post(`${environment.apiUrl}/bilat/pause`, { idBilat: id_bilat }, { observe: 'response' }).subscribe({
      next: () => {
        this.modal.openMiniHeadband("Vous êtes en pause d'une bilat.", '', 'Reprendre la bilat', (e) => {
          if (!e) this.unpauseBilatMode(id_bilat);
        });
      },
      error: (err) => {
        this.modal.openAlert('error', 'Erreur', 'Une erreur est survenue lors de la mise en pause de la bilat', () => this.modal.closeAlert());
        console.error(err);
      },
    });
  }

  private unpauseBilatMode(id_bilat: number): void {
    this.http.post(`${environment.apiUrl}/bilat/resume`, { idBilat: id_bilat }, { observe: 'response' }).subscribe({
      next: () => {
        this.router.navigate(['/quotidien/client/liste'], { queryParams: { onglet: 'lead' }, queryParamsHandling: 'merge' });
        this.modal.openMiniHeadband(
          'Vous êtes en mode bilat. Certaines fonctionnalités sont donc désactivées.',
          'Terminer la bilat',
          'Mettre en pause',
          (e) => {
            if (e) this.stopBilatMode(id_bilat);
            else this.pauseBilatMode(id_bilat);
          },
        );
      },
      error: (err) => {
        this.modal.openAlert('error', 'Erreur', 'Une erreur est survenue lors de la reprise de la bilat', () => this.modal.closeAlert());
        console.error(err);
      },
    });
  }

  private checkIfPendingBilatComment(): void {
    const pendingBilatComment = localStorage.getItem('pendingBilatComment');
    if (pendingBilatComment) {
      const bilatId = parseInt(pendingBilatComment, 10);
      if (!isNaN(bilatId)) {
        this.modal.openBilatComment(bilatId);
      } else {
        localStorage.removeItem('pendingBilatComment');
      }
    }
  }

  resetIdleTimer(): void {
    this.idle.reset();
  }


}
